import {  ContextualMenu, IconButton, IDragOptions, Link, Modal, Separator, Stack, Text } from "@fluentui/react";
import { BreakpointType } from "../../../../Container/ContainerFluid";
import { useBreakpoint } from "../../../../Container/ContainerStyles";
import { containerStyles, formContainerStyles, lgContainerStyles, lgSubTitleStyles, lgTitleContainerStyles, lgTitleStyles, mdContainerStyles, mdSubTitleStyles, mdTitleContainerStyles, mdTitleStyles, smContainerStyles, smSubTitleStyles, smTitleContainerStyles, smTitleStyles, subTitleStyles, titleContainerStyles, titleStyles, xlContainerStyles, xlSubTitleStyles, xlTitleContainerStyles, xlTitleStyles, xxlContainerStyles, xxlSubTitleStyles, xxlTitleContainerStyles, xxlTitleStyles } from "../../Common/Styles/CommonStyles";
import { RegisterPrimaryButton, RegisterSecondaryButton } from "../../Common/RegisterButton";
import { formButtonContainerStyles, infoLinkTextStyles, infoTextStyles, innerCreateTenantContainerStyles, innerCreateTenantTextStyles, innerTextStyles, lgFormButtonContainerStyles, lgInnerCreateTenantTextStyles, lgModalContentStyles, mdFormButtonContainerStyles, mdInnerCreateTenantTextStyles, mdModalContentStyles, modalContentStyles, modalIconButtonStyles, smFormButtonContainerStyles, smInnerCreateTenantTextStyles, smModalContentStyles, xlFormButtonContainerStyles, xlInnerCreateTenantTextStyles, xlModalContentStyles, xxlFormButtonContainerStyles, xxlInnerCreateTenantTextStyles, xxlModalContentStyles } from "./Styles/RegisterTrialTenantStyles";
import { RegisterTextField } from "../../Common/RegisterField";
import { useEffect, useState } from "react";
import { IRegisterContext, useRegisterContext } from "../../../RegisterContainer";
import { CustomerDomain, CustomerSetupConfig } from "../../../../../../Model/CustomerModels";
import { LeadToLive, LeadToLiveStep } from "../../../../../../Model/CommonModel";
import { Guid } from "guid-typescript";
import { SoSmartMicrosoftLogin } from "../../../Service/RegisterLoginMicrosoft";
import { RegisterService } from "../../../Service/RegisterService";
import { MessageType, ToastService } from "../../../../../../Core/Toast/ToastService";
import { useNavigate } from "react-router-dom";
import { HttpHelper } from "../../../../../../Core/Http/HttpHelper";
import { Trans } from "react-i18next";

export const RegisterTrialTenant: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const registerContext: IRegisterContext = useRegisterContext();
    const [domainName, setDomainName] = useState<string>(registerContext.leadToLive.customerDomains ? registerContext.leadToLive.customerDomains[0].tenantDomain : "");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const navigate = useNavigate();
    
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);
    
    const modalDragOptions: IDragOptions = {
        moveMenuItemText: 'Move',
        closeMenuItemText: 'Close',
        menu: ContextualMenu,
        keepInBounds: true,
        dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
    }
    
    const validateDomainName = (domain: string) => {
        let validatedDomain = domain.replace(/[^a-z0-9]/gi, '');
        setDomainName(validatedDomain +".onmicrosoft.com");
    };

    useEffect(() => {
        if(registerContext.leadToLive.currentStep !== LeadToLiveStep.TenantMode)
            return;

        registerContext.setFooterButtonProps({
            ...registerContext.footerButtonProps,
            nextButtonHidden: true
        });

        const code: string = HttpHelper.getParameter("code");
        const state: string = HttpHelper.getParameter("state");
        window.history.replaceState(null, "", HttpHelper.removeParameter("state"));
        window.history.replaceState(null, "", HttpHelper.removeParameter("code"));
        window.history.replaceState(null, "", HttpHelper.removeParameter("session_state"));

        if(code && state){
            registerContext.setShowLoader(true, RegisterService.translate("tenant:loaderText"));
            RegisterService.handleUserSwitchLogin(code, state).then(() => {

                const controller: AbortController = new AbortController();
                RegisterService.getTrialStatus(LeadToLiveStep.TenantMode, controller.signal).then((newLeadToLive: LeadToLive) => {
                    
                    registerContext.setShowLoader(false, "");
                    navigate(RegisterService.getStepEndpoint(newLeadToLive));
                })
                .catch((error) => {
                    console.error(error);
    
                    registerContext.setShowLoader(false, "");
                    ToastService.showMessage(MessageType.Error, error.message);
                });
            })
            .catch((error) => {
                console.error(error);

                registerContext.setShowLoader(false, "");
                ToastService.showMessage(MessageType.Error, error.message);
            });
        }
    }, []);

    const onSaveTenantMode = () => {
        if (!domainName || domainName === ".onmicrosoft.com") {
            setErrorMessage(RegisterService.translate("tenant:domainMandatory"));
            return;
        }

        let customerSetupConfig: CustomerSetupConfig = new CustomerSetupConfig();
        customerSetupConfig.hasTenant = false;
        customerSetupConfig.tenantId = "";
        customerSetupConfig.globalAdminUserName = "admin@" + domainName;
        customerSetupConfig.customerId = 0;
        customerSetupConfig.systemId = Guid.EMPTY;


        let customerDomains: CustomerDomain[] = [];
        customerDomains.push({
            customerId: 0,
            tenantId: "",
            tenantDomain: domainName,
            isInitial: true,
            isDefault: true,
            enabled: true,
            s2SEnabled: false,
            useAsDefault: true,
            systemId: Guid.EMPTY
        });

        let newLeadToLive: LeadToLive = {
            ...registerContext.leadToLive,
            customerSetupConfig: customerSetupConfig,
            customerDomains: customerDomains
        };
        registerContext.setLeadToLive(newLeadToLive);
        registerContext.onNext(newLeadToLive);
    };

    const onJoinTenant = () => {
        const redirectUri: string = window.location.origin + window.location.pathname;
        const state: string = window.location.origin + "|" + registerContext.leadToLive.lead.id;

        let userPrincipleName: string = "";
        if(registerContext.leadToLive.adminUser)
            userPrincipleName = registerContext.leadToLive.adminUser.userPrincipleName;

        const sosmartMsLogin: SoSmartMicrosoftLogin = new SoSmartMicrosoftLogin(
            redirectUri, 
            "select_account", 
            userPrincipleName, 
            ["User.Read"] , 
            state
        );

        sosmartMsLogin.runLoginMicrosoft();
    }

    return (
        <>
            <Stack
                className={
                    isSM ? 
                        smContainerStyles : 
                    isMD ? 
                        mdContainerStyles :
                    isLG ?
                        lgContainerStyles :
                    isXL ?
                        xlContainerStyles :
                    isXXL ?
                        xxlContainerStyles
                    :
                        containerStyles
                }
            >
                <Stack
                    className={
                        isSM ? 
                            smTitleContainerStyles : 
                        isMD ? 
                            mdTitleContainerStyles :
                        isLG ?
                            lgTitleContainerStyles :
                        isXL ?
                            xlTitleContainerStyles :
                        isXXL ?
                            xxlTitleContainerStyles
                        :
                            titleContainerStyles
                    }
                >
                    <Text 
                        className={
                            isSM ? 
                                smTitleStyles : 
                            isMD ? 
                                mdTitleStyles : 
                            isLG ?
                                lgTitleStyles :
                            isXL ?
                                xlTitleStyles :
                            isXXL ?
                                xxlTitleStyles
                            :
                                titleStyles
                        }
                    >
                        {RegisterService.translate("tenant:title")}
                    </Text>
                    <Text 
                        className={
                            isSM ? 
                                smSubTitleStyles : 
                            isMD ? 
                                mdSubTitleStyles :
                            isLG ?
                                lgSubTitleStyles :
                            isXL ?
                                xlSubTitleStyles :
                            isXXL ?
                                xxlSubTitleStyles
                            :
                                subTitleStyles
                        }
                    >
                        {RegisterService.translate("tenant:description")}
                        <br />
                        <Text
                            className={infoTextStyles}
                        >
                            <i>{RegisterService.translate("tenant:infoTitle")} </i>
                        </Text> 
                        <Link
                            className={infoLinkTextStyles}
                            onClick={() => {setIsModalOpen(true)}}
                            underline
                        >
                            {RegisterService.translate("tenant:infoLink")}
                        </Link>
                    </Text>
                </Stack>
                <Stack
                    className={formContainerStyles}
                    horizontal
                    horizontalAlign="center"
                >
                    <Stack
                        className={
                            isSM ? 
                                smFormButtonContainerStyles : 
                            isMD ? 
                                mdFormButtonContainerStyles : 
                            isLG ?
                                lgFormButtonContainerStyles :
                            isXL ?
                                xlFormButtonContainerStyles :
                            isXXL ?
                                xxlFormButtonContainerStyles
                            :
                                formButtonContainerStyles
                        }
                    >
                        <Stack
                            verticalFill
                            verticalAlign="center"
                        >
                            {
                                !registerContext.leadToLive.customerSetupConfig || !registerContext.leadToLive.customerSetupConfig.cspCustomerCreated ?
                                    <>
                                        <RegisterPrimaryButton 
                                            iconName="WindowsLogo"
                                            infoText=""
                                            text={RegisterService.translate("tenant:joinButton")}
                                            onClick={() => {onJoinTenant()}}
                                        />
                                        <Separator
                                            className={innerTextStyles}
                                        >
                                            {RegisterService.translate("tenant:divider")}
                                        </Separator>
                                    </>
                                :
                                    <></>
                            }
                            <Stack
                                className={innerCreateTenantContainerStyles}
                            >
                                <Text
                                    className={
                                        isSM ?
                                            smInnerCreateTenantTextStyles :
                                        isMD ?
                                            mdInnerCreateTenantTextStyles :
                                        isLG ?
                                            lgInnerCreateTenantTextStyles :
                                        isXL ?
                                            xlInnerCreateTenantTextStyles :
                                        isXXL ?
                                            xxlInnerCreateTenantTextStyles
                                        :
                                            innerCreateTenantTextStyles
                                    }
                                >
                                    {RegisterService.translate("tenant:domain")}&nbsp;<b>{domainName}</b>
                                </Text>
                                {
                                    !registerContext.leadToLive.customerSetupConfig || !registerContext.leadToLive.customerSetupConfig.cspCustomerCreated ?
                                        <>
                                            <RegisterTextField 
                                                value={domainName.substring(0, domainName.indexOf("."))}
                                                placeholder={RegisterService.translate("tenant:createNewPlaceholder")}
                                                suffix=".onmicrosoft.com"
                                                autoFocus
                                                onChange={(value: string) => {
                                                    if (value)
                                                        setErrorMessage("");

                                                    validateDomainName(value)
                                                }}
                                                maxLength={27}
                                                errorMessage={errorMessage}
                                            />
                                            <RegisterSecondaryButton 
                                                iconName="Add"
                                                infoText=""
                                                text={RegisterService.translate("tenant:createNewButton")}
                                                onClick={() => {onSaveTenantMode()}}
                                            />
                                        </>
                                    :
                                        <RegisterPrimaryButton 
                                            iconName="Next"
                                            infoText=""
                                            text= {RegisterService.translate("tenant:continueButton")}
                                            onClick={() => {onSaveTenantMode()}}
                                        />
                                }
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
            <Modal
                titleAriaId={"ModalRow"}
                isOpen={isModalOpen}
                onDismiss={() => setIsModalOpen(false)}
                isBlocking={false}
                containerClassName={
                    isSM ?
                        smModalContentStyles.container :
                    isMD ?
                        mdModalContentStyles.container :
                    isLG ?
                        lgModalContentStyles.container :
                    isXL ?
                        xlModalContentStyles.container :
                    isXXL ?
                        xxlModalContentStyles.container
                    :
                        modalContentStyles.container
                }
                dragOptions={modalDragOptions}
            >
                <div 
                    className={
                        isSM ?
                            smModalContentStyles.header :
                        isMD ?
                            mdModalContentStyles.header :
                        isLG ?
                            lgModalContentStyles.header :
                        isXL ?
                            xlModalContentStyles.header :
                        isXXL ?
                            xxlModalContentStyles.header
                        :
                            modalContentStyles.header
                    }
                >
                    <h2 
                        className={
                            isSM ?
                                smModalContentStyles.heading :
                            isMD ?
                                mdModalContentStyles.heading :
                            isLG ?
                                lgModalContentStyles.heading :
                            isXL ?
                                xlModalContentStyles.heading :
                            isXXL ?
                                xxlModalContentStyles.heading
                            :
                                modalContentStyles.heading
                        }
                    >
                        {RegisterService.translate("tenant:modalTitle")}
                    </h2>
                    <IconButton
                        styles={modalIconButtonStyles}
                        iconProps={{ iconName: 'Cancel' }}
                        ariaLabel="Close popup modal"
                        onClick={() => setIsModalOpen(false)}
                    />
                </div>
                <div
                    className={
                        isSM ?
                            smModalContentStyles.body :
                        isMD ?
                            mdModalContentStyles.body :
                        isLG ?
                            lgModalContentStyles.body :
                        isXL ?
                            xlModalContentStyles.body :
                        isXXL ?
                            xxlModalContentStyles.body
                        :
                            modalContentStyles.body
                    }
                >
                    <Trans 
                        i18nKey={"common:register:tenant:modalContent1"}
                        components={[<strong />]}
                    />
                    <br />
                    <br />
                    <Trans 
                        i18nKey={"common:register:tenant:modalContent2"}
                        components={[<strong />]}
                    />
                    <br />
                    <br />
                    <Trans 
                        i18nKey={"common:register:tenant:modalContent3"}
                        components={[<strong />]}
                    />   
                </div>
            </Modal>
        </>
    );
};